import { useState } from 'react'
import { ArrowUUpLeft, MapPin, Prohibit, UserPlus } from '@phosphor-icons/react'
import { Button, Field, IntegerInput, Modal, TextArea } from '@/ui'
import { useAssignRoll, useReturnRoll, useVoidRemainder, type RollRow } from '../api/gate'
import type { UserOption } from '../api/reference'
import { useScope } from '../api/appConfig'
import { ActionButton } from './Can'
import { ConfirmDialog } from './ConfirmDialog'
import { PersonPicker } from './PersonPicker'

/** Assign / return / void the remainder of a tag roll or seal strip (F4.1-i, F4.11-d). */
export function RollActions({
  roll,
  sessionId,
  showAssign,
}: {
  roll: RollRow
  sessionId?: string
  showAssign?: boolean
}) {
  const { v1 } = useScope()
  const [dialog, setDialog] = useState<'assign' | 'return' | 'void' | null>(null)
  const returned = Boolean(roll.returnedAt)
  if (v1) {
    // V1 (F4.1): a roll belongs to its village, not to an operator; no evening return or void.
    return (
      <div className="flex flex-wrap gap-2">
        {/* Only bag tag rolls and seal strips go to a village; machine, location, output-bag and
            QC-seal labels are stuck on where they belong. */}
        {showAssign && !roll.issuedAt && (roll.kind === 'BAG' || roll.kind === 'SEAL') && (
          <ActionButton
            perm="qr-batches:assign"
            size="sm"
            variant="secondary"
            leadingIcon={<MapPin />}
            onClick={() => setDialog('assign')}
          >
            Give to {roll.villageCode ?? 'its village'}
          </ActionButton>
        )}
        {roll.issuedAt && (roll.kind === 'BAG' || roll.kind === 'SEAL') && (
          <span className="text-[13px] text-muted">
            In use at {roll.villageCode ?? 'the village'}
          </span>
        )}
        <VillageAssignDialog
          open={dialog === 'assign'}
          onClose={() => setDialog(null)}
          roll={roll}
        />
      </div>
    )
  }
  return (
    <div className="flex flex-wrap gap-2">
      {showAssign && !returned && (
        <ActionButton
          perm="qr-batches:assign"
          size="sm"
          variant="secondary"
          leadingIcon={<UserPlus />}
          onClick={() => setDialog('assign')}
        >
          {roll.issuedToId ? 'Reassign' : 'Assign'}
        </ActionButton>
      )}
      {roll.issuedToId && !returned && (
        <ActionButton
          perm="qr-batches:return"
          size="sm"
          variant="secondary"
          leadingIcon={<ArrowUUpLeft />}
          onClick={() => setDialog('return')}
        >
          Return
        </ActionButton>
      )}
      {roll.unused > 0 && (
        <ActionButton
          perm="qr-batches:void-remainder"
          size="sm"
          variant="ghost"
          leadingIcon={<Prohibit />}
          onClick={() => setDialog('void')}
        >
          Void unused
        </ActionButton>
      )}
      <AssignDialog open={dialog === 'assign'} onClose={() => setDialog(null)} roll={roll} />
      <ReturnDialog
        open={dialog === 'return'}
        onClose={() => setDialog(null)}
        roll={roll}
        sessionId={sessionId}
      />
      <VoidDialog open={dialog === 'void'} onClose={() => setDialog(null)} roll={roll} />
    </div>
  )
}

/** V1: the roll goes to the village printed on it; every phone of that village can use it. */
function VillageAssignDialog({
  open,
  onClose,
  roll,
}: {
  open: boolean
  onClose: () => void
  roll: RollRow
}) {
  const assign = useAssignRoll()
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={`Give this ${roll.kind === 'SEAL' ? 'seal strip' : 'tag roll'} to ${roll.villageCode ?? 'its village'}`}
      description={`${roll.villageCode ?? ''} ${roll.startSequence ?? ''}–${roll.endSequence ?? ''} · ${roll.count} codes. Every phone of the village gets it with its next roster download; no scan at the gate.`}
      confirmLabel="Give to the village"
      loading={assign.isPending}
      onConfirm={() => assign.mutateAsync({ id: roll.id }).then(onClose)}
    />
  )
}

function AssignDialog({
  open,
  onClose,
  roll,
}: {
  open: boolean
  onClose: () => void
  roll: RollRow
}) {
  const [person, setPerson] = useState<UserOption | null>(null)
  const [spare, setSpare] = useState(false)
  const assign = useAssignRoll()
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Assign ${roll.kind === 'SEAL' ? 'seal strip' : 'tag roll'}`}
      description={`${roll.villageCode ?? ''} ${roll.startSequence ?? ''}–${roll.endSequence ?? ''} · ${roll.count} codes. Codes become ISSUED to this operator.`}
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!person}
            loading={assign.isPending}
            onClick={() =>
              person &&
              void assign
                .mutateAsync({ id: roll.id, toUserId: person.id, spare })
                .then(onClose)
                .catch(() => undefined)
            }
          >
            Assign
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Operator" required>
          <PersonPicker
            value={person}
            onChange={setPerson}
            placeholder="Search the operator"
            label="Operator"
          />
        </Field>
        <label className="flex items-center gap-2 text-[14px]">
          <input
            type="checkbox"
            checked={spare}
            onChange={(e) => setSpare(e.target.checked)}
            className="h-4 w-4 accent-amber"
          />
          Spare roll (kept in the kit, not in use yet)
        </label>
      </div>
    </Modal>
  )
}

function ReturnDialog({
  open,
  onClose,
  roll,
  sessionId,
}: {
  open: boolean
  onClose: () => void
  roll: RollRow
  sessionId?: string
}) {
  const [count, setCount] = useState<number | null>(null)
  const [kept, setKept] = useState(false)
  const [note, setNote] = useState('')
  const ret = useReturnRoll()
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Roll return"
      description={`${roll.unused} codes should still be unused on this ${roll.kind === 'SEAL' ? 'strip' : 'roll'}. A different count raises an exception.`}
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={ret.isPending}
            disabled={!kept && count === null}
            onClick={() =>
              void ret
                .mutateAsync({
                  id: roll.id,
                  countReceived: kept ? undefined : (count ?? undefined),
                  keptByOperator: kept || undefined,
                  sessionId,
                  note: note || undefined,
                })
                .then(onClose)
                .catch(() => undefined)
            }
          >
            Record return
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex items-center gap-2 text-[14px]">
          <input
            type="checkbox"
            checked={kept}
            onChange={(e) => setKept(e.target.checked)}
            className="h-4 w-4 accent-amber"
          />
          The operator keeps it for the next day
        </label>
        {!kept && (
          <Field label="Unused codes counted back" required hint={`Expected ${roll.unused}`}>
            <IntegerInput value={count} onChange={setCount} />
          </Field>
        )}
        <Field label="Note">
          <TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={300}
          />
        </Field>
      </div>
    </Modal>
  )
}

function VoidDialog({
  open,
  onClose,
  roll,
}: {
  open: boolean
  onClose: () => void
  roll: RollRow
}) {
  const v = useVoidRemainder()
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title="Void the unused codes"
      description={`${roll.unused} unused codes on this ${roll.kind === 'SEAL' ? 'strip' : 'roll'} become VOID for good. A void tag seen later raises TAG_VOID_SEEN.`}
      confirmLabel="Void unused codes"
      tone="danger"
      loading={v.isPending}
      reason={{ label: 'Reason', minLength: 4, placeholder: 'e.g. roll lost in the rain' }}
      onConfirm={(reason) => v.mutateAsync({ id: roll.id, reason }).then(onClose)}
    />
  )
}
